import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import {
    LAST_VIEWED_ADVERT_ID,
    getItemSessionStorage,
    removeItemSessionStorage,
    setItemSessionStorage,
} from '@at/window-utils'

type PagesLoadedProps = {
    initialPage: number
    nextPage: number
    previousPage: number
    setLoadedPages: (pageNumbers: number[]) => void
}

const RESTORED_INITIAL_PAGE = 'initialQueryPage'

const PagesLoadedContext = createContext<PagesLoadedProps>({
    initialPage: 0,
    nextPage: 0,
    previousPage: 0,
    setLoadedPages: () => {},
})
PagesLoadedContext.displayName = 'PagesLoadedContext'

export const getPages = (
    pageNumbers: number[]
): {
    nextPageNumber: number
    previousPageNumber: number
} => {
    pageNumbers = [...new Set(pageNumbers)].sort((a, b) => a - b)
    return {
        nextPageNumber: pageNumbers[pageNumbers.length - 1] + 1 || 1,
        previousPageNumber: pageNumbers[0] - 1 || 0,
    }
}

export const PagesLoadedContextProvider = ({ children }: React.PropsWithChildren<object>) => {
    const { search } = useLocation()
    const [page, setPage] = useState(1)
    const [initialPage, setInitialPage] = useState(0)
    const [nextPage, setNextPage] = useState(0)
    const [previousPage, setPreviousPage] = useState(0)

    const setLoadedPages = useCallback((pageNumbers: number[] = []) => {
        const { nextPageNumber, previousPageNumber } = getPages(pageNumbers)
        setNextPage(nextPageNumber)
        setPreviousPage(previousPageNumber)
    }, [])

    useEffect(() => {
        const queryParams = new URLSearchParams(search)
        setPage(Math.abs(Number(queryParams.get('page'))))

        if (!initialPage || page === 0) {
            let _initialPage = page || 1

            const returnedFromAdvert = getItemSessionStorage(LAST_VIEWED_ADVERT_ID)
            const restoredInitialPage = getItemSessionStorage(RESTORED_INITIAL_PAGE)
            if (returnedFromAdvert && restoredInitialPage) {
                _initialPage = JSON.parse(restoredInitialPage) ?? _initialPage
            } else {
                removeItemSessionStorage(RESTORED_INITIAL_PAGE)
            }

            setInitialPage(_initialPage)
            setItemSessionStorage(RESTORED_INITIAL_PAGE, JSON.stringify(_initialPage))
        }
    }, [initialPage, page, search])

    const PageLoadedValues = {
        initialPage,
        nextPage,
        previousPage,
        setLoadedPages,
    }

    return <PagesLoadedContext.Provider value={PageLoadedValues}>{children}</PagesLoadedContext.Provider>
}

export const usePagesLoadedContext = () => {
    return useContext(PagesLoadedContext)
}
